import { useState, useEffect, useCallback } from 'react';
import * as sdk from 'matrix-js-sdk';

const HOMESERVER_URL = 'https://r3ich.com';
const STORAGE_KEY = 'observer_matrix_session';
const OIDC_STATE_KEY = 'observer_oidc_state';
const CLIENT_ID_KEY = 'observer_oidc_client_id';

// Generate random string for PKCE and state
function generateRandomString(length = 64) {
  const array = new Uint8Array(length);
  crypto.getRandomValues(array);
  return Array.from(array, byte => byte.toString(16).padStart(2, '0')).join('').substring(0, length);
}

// Generate PKCE code verifier and challenge
async function generatePKCE() {
  const verifier = generateRandomString(64);
  const encoder = new TextEncoder();
  const data = encoder.encode(verifier);
  const hash = await crypto.subtle.digest('SHA-256', data);
  const challenge = btoa(String.fromCharCode(...new Uint8Array(hash)))
    .replace(/\+/g, '-')
    .replace(/\//g, '_')
    .replace(/=+$/, '');
  return { verifier, challenge };
}

export function useMatrixClient() {
  const [client, setClient] = useState(null);
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [oidcConfig, setOidcConfig] = useState(null);

  // Fetch OIDC configuration on mount
  useEffect(() => {
    const fetchOidcConfig = async () => {
      try {
        // Get Matrix well-known
        const wellKnown = await fetch(`${HOMESERVER_URL}/.well-known/matrix/client`).then(r => r.json());

        if (wellKnown['org.matrix.msc2965.authentication']) {
          const issuer = wellKnown['org.matrix.msc2965.authentication'].issuer;
          // Get OIDC configuration
          const oidc = await fetch(`${issuer}.well-known/openid-configuration`).then(r => r.json());
          setOidcConfig({ issuer, ...oidc });
        }
      } catch (e) {
        console.error('Failed to fetch OIDC config:', e);
      }
    };

    fetchOidcConfig();
  }, []);

  // Handle OIDC callback on mount
  useEffect(() => {
    const handleCallback = async () => {
      const urlParams = new URLSearchParams(window.location.search);
      const code = urlParams.get('code');
      const state = urlParams.get('state');
      const errorParam = urlParams.get('error');

      if (errorParam) {
        setError(urlParams.get('error_description') || errorParam);
        window.history.replaceState({}, '', window.location.pathname);
        setIsLoading(false);
        return;
      }

      if (!code || !state) {
        // Not a callback, try to restore session
        await restoreSession();
        return;
      }

      // Get stored OIDC state
      const storedState = sessionStorage.getItem(OIDC_STATE_KEY);
      if (!storedState) {
        setError('No OIDC state found');
        window.history.replaceState({}, '', window.location.pathname);
        setIsLoading(false);
        return;
      }

      const { state: expectedState, verifier, homeserverUrl, issuer, deviceId } = JSON.parse(storedState);

      if (state !== expectedState) {
        setError('Invalid OIDC state');
        sessionStorage.removeItem(OIDC_STATE_KEY);
        window.history.replaceState({}, '', window.location.pathname);
        setIsLoading(false);
        return;
      }

      try {
        // Get OIDC config
        const oidc = await fetch(`${issuer}.well-known/openid-configuration`).then(r => r.json());

        // Get stored client ID from dynamic registration
        const clientId = localStorage.getItem(CLIENT_ID_KEY);
        if (!clientId) {
          throw new Error('No client ID found. Please try logging in again.');
        }

        // Exchange code for tokens
        const tokenResponse = await fetch(oidc.token_endpoint, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/x-www-form-urlencoded',
          },
          body: new URLSearchParams({
            grant_type: 'authorization_code',
            code: code,
            redirect_uri: window.location.origin + '/',
            client_id: clientId,
            code_verifier: verifier,
          }),
        });

        if (!tokenResponse.ok) {
          const errorData = await tokenResponse.json();
          throw new Error(errorData.error_description || errorData.error || 'Token exchange failed');
        }

        const tokens = await tokenResponse.json();

        // Get user ID by calling whoami on the Matrix homeserver
        // The access token from MAS is valid for Matrix API calls
        const whoamiResponse = await fetch(`${homeserverUrl}/_matrix/client/v3/account/whoami`, {
          headers: {
            'Authorization': `Bearer ${tokens.access_token}`,
          },
        });

        if (!whoamiResponse.ok) {
          throw new Error('Failed to get user identity from homeserver');
        }

        const whoami = await whoamiResponse.json();
        const userId = whoami.user_id;

        // Create token refresh function for matrix-js-sdk
        const tokenRefreshFunction = async (refreshToken) => {
          console.log('[Matrix] Refreshing token...');
          const refreshResponse = await fetch(oidc.token_endpoint, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/x-www-form-urlencoded',
            },
            body: new URLSearchParams({
              grant_type: 'refresh_token',
              refresh_token: refreshToken,
              client_id: clientId,
            }),
          });

          if (!refreshResponse.ok) {
            throw new Error('Token refresh failed');
          }

          const newTokens = await refreshResponse.json();
          console.log('[Matrix] Token refreshed successfully');

          // Update stored session
          const storedSession = localStorage.getItem(STORAGE_KEY);
          if (storedSession) {
            const sessionData = JSON.parse(storedSession);
            sessionData.accessToken = newTokens.access_token;
            if (newTokens.refresh_token) {
              sessionData.refreshToken = newTokens.refresh_token;
            }
            localStorage.setItem(STORAGE_KEY, JSON.stringify(sessionData));
          }

          return {
            accessToken: newTokens.access_token,
            refreshToken: newTokens.refresh_token || refreshToken,
          };
        };

        const actualDeviceId = deviceId || `OBS_${Date.now()}`;
        console.log('[Matrix] Creating client with device ID:', actualDeviceId);

        // Create Matrix client with the access token and refresh function
        const matrixClient = sdk.createClient({
          baseUrl: homeserverUrl,
          accessToken: tokens.access_token,
          refreshToken: tokens.refresh_token,
          userId: userId,
          deviceId: actualDeviceId,
          tokenRefreshFunction: tokenRefreshFunction,
        });

        // Initialize crypto - this uploads device keys to the server
        // which is required for other participants to send us E2EE keys via to-device messages
        console.log('[Matrix] Initializing Rust crypto...');
        try {
          await matrixClient.initRustCrypto({
            useIndexedDB: true,
          });
          console.log('[Matrix] Rust crypto initialized');

          // Force upload device keys
          const crypto = matrixClient.getCrypto();
          if (crypto) {
            console.log('[Matrix] Uploading device keys...');
            // This uploads our device keys to the server so other users can find us
            await crypto.userHasCrossSigningKeys();
            console.log('[Matrix] Device keys should now be uploaded');
          }
        } catch (cryptoError) {
          console.error('[Matrix] Crypto init failed:', cryptoError);
        }

        // Start the client
        console.log('[Matrix] Starting client...');
        await matrixClient.startClient({ initialSyncLimit: 10 });

        // Wait for initial sync
        await new Promise((resolve, reject) => {
          const timeout = setTimeout(() => reject(new Error('Sync timeout')), 30000);
          matrixClient.once(sdk.ClientEvent.Sync, (syncState) => {
            clearTimeout(timeout);
            if (syncState === 'PREPARED' || syncState === 'SYNCING') {
              resolve();
            } else {
              reject(new Error(`Sync failed: ${syncState}`));
            }
          });
        });

        // Save session
        const session = {
          homeserverUrl,
          accessToken: tokens.access_token,
          refreshToken: tokens.refresh_token,
          userId: userId,
          deviceId: matrixClient.getDeviceId(),
          issuer: issuer,
          tokenEndpoint: oidc.token_endpoint,
          clientId: clientId,
        };
        localStorage.setItem(STORAGE_KEY, JSON.stringify(session));

        setClient(matrixClient);
        setIsLoggedIn(true);

        // Clean up
        sessionStorage.removeItem(OIDC_STATE_KEY);
        window.history.replaceState({}, '', window.location.pathname);

      } catch (e) {
        console.error('OIDC callback failed:', e);
        setError(e.message || 'Authentication failed');
        sessionStorage.removeItem(OIDC_STATE_KEY);
        window.history.replaceState({}, '', window.location.pathname);
      }

      setIsLoading(false);
    };

    handleCallback();
  }, []);

  const restoreSession = async () => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (stored) {
        const session = JSON.parse(stored);

        // Try to refresh token if we have a refresh token
        if (session.refreshToken && session.tokenEndpoint && session.clientId) {
          try {
            const refreshResponse = await fetch(session.tokenEndpoint, {
              method: 'POST',
              headers: {
                'Content-Type': 'application/x-www-form-urlencoded',
              },
              body: new URLSearchParams({
                grant_type: 'refresh_token',
                refresh_token: session.refreshToken,
                client_id: session.clientId,
              }),
            });

            if (refreshResponse.ok) {
              const tokens = await refreshResponse.json();
              session.accessToken = tokens.access_token;
              if (tokens.refresh_token) {
                session.refreshToken = tokens.refresh_token;
              }
              localStorage.setItem(STORAGE_KEY, JSON.stringify(session));
              console.log('[Matrix] Token refreshed on restore');
            } else {
              // Refresh failed, clear session to force re-login
              console.warn('[Matrix] Token refresh failed on restore, clearing session');
              localStorage.removeItem(STORAGE_KEY);
              setIsLoading(false);
              return;
            }
          } catch (e) {
            console.warn('[Matrix] Token refresh error, clearing session:', e);
            localStorage.removeItem(STORAGE_KEY);
            setIsLoading(false);
            return;
          }
        }

        // Create token refresh function for matrix-js-sdk
        const tokenRefreshFunction = async (refreshToken) => {
          console.log('[Matrix] Refreshing token...');
          const refreshResponse = await fetch(session.tokenEndpoint, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/x-www-form-urlencoded',
            },
            body: new URLSearchParams({
              grant_type: 'refresh_token',
              refresh_token: refreshToken,
              client_id: session.clientId,
            }),
          });

          if (!refreshResponse.ok) {
            throw new Error('Token refresh failed');
          }

          const newTokens = await refreshResponse.json();
          console.log('[Matrix] Token refreshed successfully');

          // Update stored session
          session.accessToken = newTokens.access_token;
          if (newTokens.refresh_token) {
            session.refreshToken = newTokens.refresh_token;
          }
          localStorage.setItem(STORAGE_KEY, JSON.stringify(session));

          return {
            accessToken: newTokens.access_token,
            refreshToken: newTokens.refresh_token || refreshToken,
          };
        };

        console.log('[Matrix] Restoring session with device ID:', session.deviceId);

        const matrixClient = sdk.createClient({
          baseUrl: session.homeserverUrl,
          accessToken: session.accessToken,
          refreshToken: session.refreshToken,
          userId: session.userId,
          deviceId: session.deviceId,
          tokenRefreshFunction: tokenRefreshFunction,
        });

        // Verify token is still valid
        await matrixClient.whoami();
        console.log('[Matrix] Token verified');

        // Initialize crypto - this uploads device keys to the server
        // which is required for other participants to send us E2EE keys via to-device messages
        console.log('[Matrix] Initializing Rust crypto (restore)...');
        try {
          await matrixClient.initRustCrypto({
            useIndexedDB: true,
          });
          console.log('[Matrix] Rust crypto initialized (restore)');

          // Force upload device keys
          const crypto = matrixClient.getCrypto();
          if (crypto) {
            console.log('[Matrix] Uploading device keys (restore)...');
            await crypto.userHasCrossSigningKeys();
            console.log('[Matrix] Device keys should now be uploaded (restore)');
          }
        } catch (cryptoError) {
          console.error('[Matrix] Crypto init failed (restore):', cryptoError);
        }

        console.log('[Matrix] Starting client (restore)...');
        await matrixClient.startClient({ initialSyncLimit: 10 });

        // Wait for initial sync
        await new Promise((resolve) => {
          matrixClient.once(sdk.ClientEvent.Sync, (state) => {
            if (state === 'PREPARED' || state === 'SYNCING') {
              resolve();
            }
          });
        });

        setClient(matrixClient);
        setIsLoggedIn(true);
      }
    } catch (e) {
      console.error('Failed to restore session:', e);
      localStorage.removeItem(STORAGE_KEY);
    }
    setIsLoading(false);
  };

  // Start OIDC login flow
  const login = useCallback(async (homeserverUrl = HOMESERVER_URL) => {
    setError(null);
    setIsLoading(true);

    try {
      // Get Matrix well-known
      const wellKnown = await fetch(`${homeserverUrl}/.well-known/matrix/client`).then(r => r.json());

      if (!wellKnown['org.matrix.msc2965.authentication']) {
        throw new Error('This homeserver does not support MAS authentication');
      }

      const issuer = wellKnown['org.matrix.msc2965.authentication'].issuer;

      // Get OIDC configuration
      const oidc = await fetch(`${issuer}.well-known/openid-configuration`).then(r => r.json());

      // Check if we have a stored client ID or need to register dynamically
      let clientId = localStorage.getItem(CLIENT_ID_KEY);

      if (!clientId && oidc.registration_endpoint) {
        // Dynamic client registration
        const registrationResponse = await fetch(oidc.registration_endpoint, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            client_name: 'LiveKit Observer Panel',
            redirect_uris: [window.location.origin + '/'],
            token_endpoint_auth_method: 'none',
            response_types: ['code'],
            grant_types: ['authorization_code', 'refresh_token'],
            application_type: 'web',
          }),
        });

        if (!registrationResponse.ok) {
          const errorData = await registrationResponse.json();
          throw new Error(errorData.error_description || errorData.error || 'Client registration failed');
        }

        const registration = await registrationResponse.json();
        clientId = registration.client_id;
        localStorage.setItem(CLIENT_ID_KEY, clientId);
      }

      if (!clientId) {
        throw new Error('Failed to obtain client ID');
      }

      // Generate PKCE
      const { verifier, challenge } = await generatePKCE();

      // Generate state
      const state = generateRandomString(32);

      // Generate a random device ID for this session
      const deviceId = 'OBS_' + generateRandomString(12).toUpperCase();

      // Store state for callback
      sessionStorage.setItem(OIDC_STATE_KEY, JSON.stringify({
        state,
        verifier,
        homeserverUrl,
        issuer,
        deviceId,
      }));

      // Build authorization URL
      const authUrl = new URL(oidc.authorization_endpoint);
      authUrl.searchParams.set('response_type', 'code');
      authUrl.searchParams.set('client_id', clientId);
      authUrl.searchParams.set('redirect_uri', window.location.origin + '/');
      // Request Matrix API scopes - these are required for Matrix client access
      // urn:matrix:org.matrix.msc2967.client:api:* grants full Matrix API access
      // urn:matrix:org.matrix.msc2967.client:device:DEVICEID grants device-specific access
      authUrl.searchParams.set('scope', `openid urn:matrix:org.matrix.msc2967.client:api:* urn:matrix:org.matrix.msc2967.client:device:${deviceId}`);
      authUrl.searchParams.set('state', state);
      authUrl.searchParams.set('code_challenge', challenge);
      authUrl.searchParams.set('code_challenge_method', 'S256');

      // Redirect to MAS
      window.location.href = authUrl.toString();

    } catch (e) {
      console.error('Login failed:', e);
      setError(e.message || 'Login failed');
      setIsLoading(false);
    }
  }, []);

  const logout = useCallback(async () => {
    if (client) {
      try {
        await client.logout();
      } catch (e) {
        console.error('Logout error:', e);
      }
      client.stopClient();
    }
    localStorage.removeItem(STORAGE_KEY);
    setClient(null);
    setIsLoggedIn(false);
  }, [client]);

  return {
    client,
    isLoggedIn,
    isLoading,
    error,
    login,
    logout,
  };
}
